/* eslint-disable camelcase */

import { possuiConsentimentoAtivo } from '@shared/cookie-consent/utils/consentimentoAtual';
import type { IPromessa } from '@/models/IPromessa';
import type { OfertaResource } from '@/models/resources';
import * as builder from './builder';
import type { EventParameters } from './eventSignatures';

export default function sendEvent(name: string, params: Partial<EventParameters>, gaId?: string | string[]) {
  // Sem consentimento o GTM não carrega e o event_callback nunca seria chamado.
  if (!possuiConsentimentoAtivo()) return Promise.resolve();

  return new Promise<void>(resolve => {
    const event_callback = () => resolve();

    window.gtag('event', name, {
      ...params,
      event_callback,
      send_to: gaId ?? appSettings.VUE_APP_GA4_TAG_ID,
    });
  });
}

// #region - Eventos Login

export async function ga4AcessouLogin() {
  const standardParams = await builder.makeStandard(false);

  return sendEvent('acessou_login', {
    ...standardParams,
  });
}

export async function ga4ClickEmLogin(data_ua: string) {
  const standardParams = await builder.makeStandard();

  return sendEvent('click_em_login', {
    data_ua,
    ...standardParams,
  });
}

// #endregion

// #region - Eventos Cadastro

export async function ga4AcessouCadastro() {
  const standardParams = await builder.makeStandard();

  return sendEvent('acessou_cadastro', { ...standardParams });
}

export async function ga4ClickConfirmarCadastro(data_ua: string) {
  const standardParams = await builder.makeStandard();

  return sendEvent('click_confirmar_cadastro', {
    data_ua,
    ...standardParams,
  });
}

export async function ga4VisualizouEmailJaCadastrado() {
  const standardParams = await builder.makeStandard();

  return sendEvent('visualizou_email_ja_cadastrado', {
    ...standardParams,
  });
}
export async function ga4ClickAcessarConta(data_ua: string) {
  const standardParams = await builder.makeStandard();

  return sendEvent('click_acessar_conta', {
    data_ua,
    ...standardParams,
  });
}

export async function ga4ClickCadastrarComOutroEmail(data_ua: string) {
  const standardParams = await builder.makeStandard();

  return sendEvent('click_cadastrar_com_outro_email', {
    data_ua,
    ...standardParams,
  });
}

export async function ga4ClickCadastroGoogle(data_ua: string) {
  const standardParams = await builder.makeStandard();

  return sendEvent('click_cadastro_google', {
    data_ua,
    ...standardParams,
  });
}

export async function ga4AcessouConfirmarData() {
  const standardParams = await builder.makeStandard();

  return sendEvent('acessou_confirmar_data', {
    ...standardParams,
  });
}

// #endregion

// #region - Eventos Oferta

export async function ga4ClickVerOutrasOfertas(divida: builder.DividaEventArgs, data_ua: string) {
  const dividaParams = builder.makeDivida(divida);
  const standardParams = await builder.makeStandard();

  return sendEvent('click_ver_outras_ofertas', {
    ...dividaParams,
    data_ua,
    ...standardParams,
  });
}

export async function ga4ClickQueroEssaOferta(divida: builder.DividaEventArgs, data_ua: string) {
  const dividaParams = builder.makeDivida(divida);
  const standardParams = await builder.makeStandard();

  return sendEvent('click_quero_essa_oferta', {
    ...dividaParams,
    data_ua,
    ...standardParams,
  });
}

export async function ga4AcessouTipoNegociacao(divida: builder.DividaEventArgs) {
  const dividaParams = builder.makeDivida(divida);
  const standardParams = await builder.makeStandard();

  return sendEvent('acessou_tipo_negociacao', {
    ...dividaParams,
    ...standardParams,
  });
}

export async function ga4ClickConfirmarProposta(
  divida: builder.DividaEventArgs,
  oferta_id: OfertaResource['id'],
  data_ua: string
) {
  const dividaParams = builder.makeDivida(divida);
  const standardParams = await builder.makeStandard();

  return sendEvent('click_confirmar_proposta', {
    ...dividaParams,
    data_ua,
    ...standardParams,
    oferta_id,
  });
}

// #endregion

// #region - Eventos Dívida
export async function ga4DividaSincronizadaSucesso(nome_credor: string) {
  const standardParams = await builder.makeStandard();

  return sendEvent('divida_sincronizada_sucesso', {
    ...standardParams,
    nome_credor,
  });
}

export async function ga4VisualizouListaDividas(credores: string[]) {
  const standardParams = await builder.makeStandard();

  const nome_credor = Array.from(new Set(credores)).join(',');

  return sendEvent('visualizou_lista_dividas', {
    ...standardParams,
    nome_credor,
  });
}

export async function ga4AcessouSemDivida() {
  const standardParams = await builder.makeStandard();

  return sendEvent('acessou_sem_divida', {
    ...standardParams,
  });
}

export async function ga4ClickDivida(divida: builder.DividaEventArgs, data_ua: string) {
  const standardParams = await builder.makeStandard();
  const dividaParams = builder.makeDivida(divida);

  return sendEvent('click_divida', {
    ...standardParams,
    ...dividaParams,
    data_ua,
  });
}

export async function ga4VisualizouDivida(divida: builder.DividaEventArgs) {
  const standardParams = await builder.makeStandard();
  const dividaParams = builder.makeDivida(divida);

  return sendEvent('visualizou_divida', {
    ...standardParams,
    ...dividaParams,
  });
}

export async function ga4ClickDetalhesHome(divida: builder.DividaEventArgs, data_ua: string) {
  const standardParams = await builder.makeStandard();
  const dividaParams = builder.makeDivida(divida);

  return sendEvent('click_detalhes_home', {
    ...standardParams,
    ...dividaParams,
    data_ua,
  });
}

export async function ga4ClickPagamentoDetalhesAcordo(proposta: IPromessa, data_ua: string) {
  if (!proposta) return;

  const standardParams = await builder.makeStandard();
  const dividaParams = {
    nome_credor: proposta.divida.credor,
    divida_id: proposta.divida.id,
    produto: proposta.divida.produto || '',
  };

  await sendEvent('click_pagamento_detalhes_acordo', {
    ...standardParams,
    ...dividaParams,
    data_ua,
  });
}

export async function ga4AcessouDetalheDivida(divida: builder.DividaEventArgs) {
  const standardParams = await builder.makeStandard();
  const dividaParams = builder.makeDivida(divida);

  return sendEvent('acessou_detalhe_divida', {
    ...standardParams,
    ...dividaParams,
  });
}

export async function ga4AcessouDetalheAcordo(divida: builder.DividaEventArgs) {
  const standardParams = await builder.makeStandard();
  const dividaParams = builder.makeDivida(divida);

  return sendEvent('acessou_detalhe_acordo', {
    ...standardParams,
    ...dividaParams,
  });
}

export async function ga4ClickRecomecarNegociacao(proposta: IPromessa) {
  const standardParams = await builder.makeStandard();
  const dividaParams = {
    nome_credor: proposta.divida.credor,
    divida_id: proposta.divida.id,
    produto: proposta.divida.produto || '',
  };

  return sendEvent('click_recomecar_negociacao', {
    ...standardParams,
    ...dividaParams,
    proposta_id: proposta.id,
  });
}

export async function ga4AcessouAutenticacao(nome_credor: string) {
  const standardParams = await builder.makeStandard();

  return sendEvent('acessou_autenticacao', {
    ...standardParams,
    nome_credor,
  });
}

export async function ga4ClickConfimarAutenticacao(nome_credor: string, data_ua: string) {
  const standardParams = await builder.makeStandard();

  return sendEvent('click_confirmar_autenticacao', {
    ...standardParams,
    nome_credor,
    data_ua,
  });
}

export async function ga4ClickConfirmarCodigoSucesso(nome_credor: string, data_ua: string) {
  const standardParams = await builder.makeStandard();

  return sendEvent('click_confirmar_codigo_sucesso', {
    ...standardParams,
    nome_credor,
    data_ua,
  });
}

export async function ga4ClickConfirmarCodigoVazio(nome_credor: string, data_ua: string) {
  const standardParams = await builder.makeStandard();

  return sendEvent('click_confirmar_codigo_vazio', {
    ...standardParams,
    nome_credor,
    data_ua,
  });
}

export async function ga4ClickConfirmarCodigoInvalido(nome_credor: string, data_ua: string) {
  const standardParams = await builder.makeStandard();

  return sendEvent('click_confirmar_codigo_invalido', {
    ...standardParams,
    nome_credor,
    data_ua,
  });
}

export async function ga4ClickReenviarCodigo(nome_credor: string, data_ua: string) {
  const standardParams = await builder.makeStandard();

  return sendEvent('click_reenviar_codigo', {
    ...standardParams,
    nome_credor,
    data_ua,
  });
}

export async function ga4CodigoExpirado(nome_credor: string) {
  const standardParams = await builder.makeStandard();

  return sendEvent('codigo_expirado', {
    ...standardParams,
    nome_credor,
  });
}

// #endregion

// #region - Eventos Boleto
export async function ga4AcessouPagamento(proposta: IPromessa) {
  if (!proposta) return;

  const standardParams = await builder.makeStandard();
  const dividaParams = {
    nome_credor: proposta.divida.credor,
    divida_id: proposta.divida.id,
    produto: proposta.divida.produto || '',
  };

  sendEvent('acessou_pagamento', {
    ...standardParams,
    ...dividaParams,
  });
}

export async function ga4VisualizouPromessa(proposta: IPromessa) {
  const standardParams = await builder.makeStandard();
  const dividaParams = {
    nome_credor: proposta.divida.credor,
    divida_id: proposta.divida.id,
    produto: proposta.divida.produto || '',
  };

  return sendEvent('visualizou_promessa', {
    proposta_id: proposta?.id,
    ...standardParams,
    ...dividaParams,
  });
}

// #endregion

// #region - Eventos Deeplink Captação

export async function ga4AcessouLoginDeeplinkEmail() {
  const standardParams = await builder.makeStandard();

  return sendEvent('acessou_login_deeplink_email', {
    ...standardParams,
  });
}

export async function ga4ClickEntrarDeeplinkEmail() {
  const standardParams = await builder.makeStandard();

  return sendEvent('click_entrar_deeplink_email', {
    ...standardParams,
  });
}

export async function ga4ClickVerTodasAsDividas() {
  const standardParams = await builder.makeStandard();

  return sendEvent('click_ver_todas_as_dividas', {
    ...standardParams,
  });
}

export async function ga4AcessouCadastroDeeplinkEmail() {
  const standardParams = await builder.makeStandard();

  return sendEvent('acessou_cadastro_deeplink_email', {
    ...standardParams,
  });
}

export async function ga4ClickConfirmarDeeplinkEmail() {
  const standardParams = await builder.makeStandard();

  return sendEvent('click_confirmar_deeplink_email', {
    ...standardParams,
  });
}

// #region - Eventos Credito Consignado CLT

export async function ga4VisualizouOfertaCreditoClt() {
  const standardParams = await builder.makeStandard();

  return sendEvent('visualizou_oferta_credito_clt', { ...standardParams });
}

export async function ga4ClickCreditoClt(data_ua: string) {
  const standardParams = await builder.makeStandard();

  return sendEvent('click_credito_clt', { ...standardParams, data_ua });
}

/** Funil da jornada: uma etapa por evento, no parâmetro `data_ua`. */
export async function ga4EtapaCreditoClt(etapa: string) {
  const standardParams = await builder.makeStandard();

  return sendEvent('etapa_credito_clt', { ...standardParams, data_ua: etapa });
}

// #endregion
